import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TbReceipt, TbChevronDown } from 'react-icons/tb';
import CONFIG from '../config';

// 원장 전체를 백그라운드로 받아둔다 — 스레드 그룹 합계를 정확히 내려면 부분
// 페이지로는 부족하다(경계에 걸친 차감이 합계에서 빠진다). 요청당 200건은
// 백엔드 클램프 상한과 같은 값.
const CHUNK = 200;
const MAX_ROWS = 5000;
// "더 보기"는 표시 행(그룹) 기준 20개씩
const PAGE_SIZE = 20;

// App.js 가 이 화면을 조건부 렌더링해서 사이드바를 오갈 때마다 언마운트된다.
// 매번 원장을 통째로 다시 받으면 요청이 금방 쌓이므로(/get_credit_history 는
// rate limit 카테고리가 없어 default 200/hour) 잠깐 캐시해 재진입은 무요청.
const CACHE_TTL_MS = 60 * 1000;
let historyCache = null; // { userId, items, at }

// 딸깍체크(이 앱)에서 발생한 차감 판별 — metadata.service 가 1차,
// service 가 없던 옛 행을 위해 type 으로도 폴백한다.
const CHECK_SERVICES = new Set(['ddalkkak_check', 'ddalkkak_text_extract']);
const CHECK_TYPES = new Set([
  'usage_typo_check',
  'usage_typo_check_all',
  'usage_translation',
  'usage_text_extract',
  'usage_check',
]);

// 부가기능 차감은 항상 채팅 차감에 동반되는 내부 유형이라 배지에서는 숨긴다
// (금액은 그룹 합계에 그대로 포함).
const HIDDEN_BADGES = new Set(['usage_addon']);

const isCheckItem = (item) =>
  CHECK_SERVICES.has(item.service) || CHECK_TYPES.has(item.type);

/** 유형 배지 키 — usage_media 는 영상/이미지로 세분(옛 행은 모델명으로 추정). */
const badgeTypeOf = (item) => {
  if (item.type !== 'usage_media') return item.type;
  let kind = item.media_kind;
  if (!kind) {
    const model = (item.model || '').toLowerCase();
    if (model) kind = /video|veo|seedance/.test(model) ? 'video' : 'image';
  }
  if (kind === 'video') return 'usage_media_video';
  if (kind === 'image') return 'usage_media_image';
  return item.type;
};

/** 표시 대상 — 딸깍체크는 PPT 파일명, 딸깍 본진은 채팅 제목. */
const targetOf = (item) => {
  if (item.ppt_filename) return item.ppt_filename;
  if (item.thread_title) return item.thread_title;
  if (item.thread_id) return item.thread_id.slice(0, 8);
  return null;
};

const CreditHistoryPage = ({ userId, isMobile = false }) => {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  // 백그라운드 전체 로드 완료 여부 — 완료 전엔 총 건수를 감춘다(숫자 출렁임 방지)
  const [loadedAll, setLoadedAll] = useState(false);
  const [filter, setFilter] = useState('usage');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    if (!userId) return undefined;

    if (
      historyCache &&
      historyCache.userId === userId &&
      Date.now() - historyCache.at < CACHE_TTL_MS
    ) {
      setItems(historyCache.items);
      setLoadedAll(true);
      return undefined;
    }

    let alive = true;
    setItems([]);
    setLoadedAll(false);

    (async () => {
      const all = [];
      const seenIds = new Set();
      let totalRows = Infinity;
      let retried = false;

      while (all.length < totalRows && all.length < MAX_ROWS) {
        let res;
        try {
          res = await fetch(
            `${CONFIG.CLOUD_FUNCTION_URL}/get_credit_history` +
              `?userId=${encodeURIComponent(userId)}&limit=${CHUNK}&offset=${all.length}`,
            { credentials: 'include' }
          );
        } catch (e) {
          if (retried) break;
          retried = true;
          continue;
        }
        // 429 는 재시도해도 같은 결과 — 받아둔 만큼만 쓰고 중단한다
        if (res.status === 429) break;
        if (!res.ok) {
          if (retried) break;
          retried = true;
          continue;
        }
        retried = false;

        const data = await res.json().catch(() => null);
        if (!data || !data.success) break;

        const chunk = data.history || [];
        if (typeof data.total === 'number') totalRows = data.total;
        if (chunk.length === 0) break;

        const fresh = chunk.filter((i) => !seenIds.has(i.id));
        // 전부 중복이면 offset 이 진행되지 않아 같은 요청이 반복된다 — 종료
        if (fresh.length === 0) break;

        fresh.forEach((i) => seenIds.add(i.id));
        all.push(...fresh);
        if (!alive) return;
        setItems([...all]);
      }

      if (!alive) return;
      // 중간에 끊겼어도(429·네트워크) 받아둔 만큼 캐시해 재요청 폭주를 막는다
      historyCache = { userId, items: all, at: Date.now() };
      setLoadedAll(true);
    })();

    return () => {
      alive = false;
    };
  }, [userId]);

  // 탭 전환 시 표시 개수 리셋
  useEffect(() => setVisibleCount(PAGE_SIZE), [filter]);

  // 스레드 단위 1행 — 딸깍 본진 채팅은 한 대화에서 차감이 수십 건 생겨 목록을
  // 도배하므로 thread_id 로 묶는다(변동=합계, 일시·잔액=그룹의 최신 행).
  // 딸깍체크 차감은 thread_id 가 없어 자동으로 실행 1건 = 1행으로 남는다.
  const displayRows = useMemo(() => {
    const filtered = items.filter((item) => {
      const amount = item.amount || 0;
      if (filter === 'balance') return amount > 0;
      if (filter === 'check') return amount <= 0 && isCheckItem(item);
      return amount <= 0;
    });

    const rows = [];
    const groups = new Map();

    for (const item of filtered) {
      const hidden = HIDDEN_BADGES.has(item.type);
      const badge = badgeTypeOf(item);
      const group = item.thread_id ? groups.get(item.thread_id) : undefined;

      if (group) {
        group.amount += item.amount || 0;
        if (!hidden && !group.types.includes(badge)) group.types.push(badge);
        continue;
      }

      const row = {
        key: item.thread_id ? `t-${item.thread_id}` : String(item.id),
        types: hidden ? [] : [badge],
        target: targetOf(item),
        deleted: !!item.thread_deleted,
        amount: item.amount || 0,
        balance: item.remaining_balance,
        date: item.timestamp,
      };
      if (item.thread_id) groups.set(item.thread_id, row);
      rows.push(row);
    }

    return rows;
  }, [items, filter]);

  const visibleRows = displayRows.slice(0, visibleCount);
  const loading = !loadedAll && items.length === 0;

  const locale = i18n.language === 'en' ? 'en-US' : 'ko-KR';
  const formatDate = (ts) => {
    if (!ts) return '-';
    try {
      return new Date(ts).toLocaleString(locale, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '-';
    }
  };

  const labelOf = (type) =>
    t(`creditHistory.operationLabels.${type}`, { defaultValue: type });

  const TABS = [
    { key: 'usage', label: t('creditHistory.tabs.usage') },
    { key: 'check', label: t('creditHistory.tabs.check') },
    { key: 'balance', label: t('creditHistory.tabs.balance') },
  ];

  const renderBadges = (row) => {
    if (row.types.length === 0) return <span style={styles.muted}>-</span>;
    return row.types.map((type) => (
      <span
        key={type}
        style={{
          ...styles.typeBadge,
          ...(row.amount > 0 ? styles.typeBadgePositive : {}),
        }}
      >
        {labelOf(type)}
      </span>
    ));
  };

  const renderTarget = (row) => {
    if (!row.target) return <span style={styles.muted}>-</span>;
    return (
      <span style={styles.targetWrap}>
        <span
          style={{
            ...styles.targetText,
            ...(row.deleted ? styles.targetTextDeleted : {}),
          }}
          title={row.target}
        >
          {row.target}
        </span>
        {row.deleted && (
          <span style={styles.deletedBadge}>{t('creditHistory.deletedBadge')}</span>
        )}
      </span>
    );
  };

  const amountStyle = (amount) => ({
    ...styles.amount,
    color: amount > 0 ? '#1A62EA' : '#EF4444',
  });

  const formatAmount = (amount) =>
    `${amount > 0 ? '+' : ''}${amount.toLocaleString()}`;

  const formatBalance = (balance) =>
    typeof balance === 'number' ? balance.toLocaleString() : '-';

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <TbReceipt size={28} color="#1A62EA" style={{ marginRight: 8 }} />
          {t('creditHistory.title')}
        </h1>
      </div>

      {/* 탭 + 총 건수 */}
      <div style={styles.filterRow}>
        <div style={styles.tabGroup}>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              style={{
                ...styles.filterChip,
                ...(filter === tab.key ? styles.filterChipActive : {}),
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <span style={styles.totalCount}>
          {/* 로드 중에는 숫자가 계속 자라 보여 혼란 — 완료 후에만 표시 */}
          {loadedAll ? t('creditHistory.totalCount', { count: displayRows.length }) : '…'}
        </span>
      </div>

      {/* 목록 */}
      {loading ? (
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
        </div>
      ) : displayRows.length === 0 ? (
        <div style={styles.emptyContainer}>{t('creditHistory.empty')}</div>
      ) : (
        <div style={styles.card}>
          {isMobile ? (
            /* 모바일: 5컬럼 테이블 대신 카드 리스트 */
            <div>
              {visibleRows.map((row) => (
                <div key={row.key} style={styles.mobileRow}>
                  <div style={styles.mobileTop}>
                    <div style={styles.mobileTopLeft}>
                      {renderBadges(row)}
                      {renderTarget(row)}
                    </div>
                    <span style={amountStyle(row.amount)}>
                      {formatAmount(row.amount)}
                    </span>
                  </div>
                  <div style={styles.mobileBottom}>
                    <span style={styles.mobileDate}>{formatDate(row.date)}</span>
                    <span style={styles.mobileBalance}>
                      {t('creditHistory.table.balance')}{' '}
                      <strong>{formatBalance(row.balance)}</strong>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, width: '24%' }}>
                      {t('creditHistory.table.date')}
                    </th>
                    <th style={{ ...styles.th, width: '22%' }}>
                      {t('creditHistory.table.type')}
                    </th>
                    <th style={styles.th}>{t('creditHistory.table.target')}</th>
                    <th style={{ ...styles.th, width: '14%', textAlign: 'right' }}>
                      {t('creditHistory.table.amount')}
                    </th>
                    <th style={{ ...styles.th, width: '14%', textAlign: 'right' }}>
                      {t('creditHistory.table.balance')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => (
                    <tr key={row.key} style={styles.tr}>
                      <td style={{ ...styles.td, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                        {formatDate(row.date)}
                      </td>
                      <td style={styles.td}>
                        <div style={styles.badgeWrap}>{renderBadges(row)}</div>
                      </td>
                      <td style={styles.td}>{renderTarget(row)}</td>
                      <td style={{ ...styles.td, ...amountStyle(row.amount) }}>
                        {formatAmount(row.amount)}
                      </td>
                      <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>
                        {formatBalance(row.balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {displayRows.length > visibleCount && (
            <div style={styles.moreRow}>
              {/* 개수 표기는 로드 중 분모가 계속 커져 보여 혼란 — 화살표만 */}
              <button
                type="button"
                onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                title={t('creditHistory.more')}
                aria-label={t('creditHistory.more')}
                style={styles.moreButton}
              >
                <TbChevronDown size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    width: '100%',
    maxWidth: 1000,
    margin: '0 auto',
    padding: '32px 24px',
  },
  header: {
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    color: '#1a1a1a',
    margin: 0,
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 20,
    paddingBottom: 8,
    borderBottom: '1px solid #e5e7eb',
  },
  tabGroup: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  filterChip: {
    padding: '6px 16px',
    borderRadius: 20,
    border: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
    color: '#6b7280',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
  },
  filterChipActive: {
    backgroundColor: '#1A62EA',
    color: '#fff',
    border: '1px solid #1A62EA',
  },
  totalCount: {
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 600,
    color: '#9ca3af',
    whiteSpace: 'nowrap',
  },
  card: {
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#fff',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    minWidth: 720,
    tableLayout: 'fixed',
    borderCollapse: 'collapse',
    fontSize: 14,
  },
  th: {
    padding: '12px 16px',
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
    textAlign: 'left',
    color: '#6b7280',
    fontWeight: 600,
    fontSize: 12,
  },
  tr: {
    borderBottom: '1px solid #f3f4f6',
  },
  td: {
    padding: '12px 16px',
    fontSize: 13,
    verticalAlign: 'middle',
  },
  badgeWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
  },
  typeBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 6,
    backgroundColor: '#f3f4f6',
    color: '#4b5563',
    fontSize: 11,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  typeBadgePositive: {
    backgroundColor: '#eff6ff',
    color: '#1A62EA',
  },
  targetWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
  },
  targetText: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 600,
    color: '#374151',
  },
  targetTextDeleted: {
    color: '#9ca3af',
  },
  deletedBadge: {
    flexShrink: 0,
    padding: '1px 6px',
    borderRadius: 4,
    backgroundColor: '#f3f4f6',
    color: '#9ca3af',
    fontSize: 10,
    fontWeight: 700,
  },
  amount: {
    textAlign: 'right',
    fontWeight: 700,
  },
  muted: {
    color: '#9ca3af',
  },
  mobileRow: {
    padding: '14px 16px',
    borderBottom: '1px solid #f3f4f6',
  },
  mobileTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  mobileTopLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    flex: 1,
  },
  mobileBottom: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 8,
    fontSize: 11,
  },
  mobileDate: {
    color: '#9ca3af',
  },
  mobileBalance: {
    flexShrink: 0,
    color: '#6b7280',
  },
  moreRow: {
    display: 'flex',
    justifyContent: 'center',
    padding: '10px 0',
    borderTop: '1px solid #f3f4f6',
  },
  moreButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: '1px solid #e5e7eb',
    backgroundColor: '#fff',
    color: '#6b7280',
    cursor: 'pointer',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: 60,
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid #e5e7eb',
    borderTop: '3px solid #1A62EA',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  emptyContainer: {
    textAlign: 'center',
    padding: 60,
    color: '#9ca3af',
    fontSize: 16,
  },
};

export default CreditHistoryPage;
